import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ButtonAutoplayBoxStyle, CounterBoxStyle, IconBoxStyle, ProgressBoxStyle } from "./ButtonAutoplayBox.styles";
import { IconAutoplay } from "./IconAutoplay/ArrowRight.component";

interface ButtonAutoplay {
  duration:number,
  icon:string,
  widthMainComponent:string,
  hideCountDownButton:() => void,
  playbackStatus:string,
  startNextVideo:() => void,
  onEndAction?:() => void
}

export function ButtonAutoplay({ duration ,icon, widthMainComponent,hideCountDownButton,playbackStatus,startNextVideo,onEndAction }:ButtonAutoplay){

  const theme = useTheme();
  const { t } = useTranslation();

  const isMobile = useMediaQuery(theme.breakpoints.down(700));
  let standardWidthSVG = !isMobile ? "26": "16";

  const [isHovered, setIsHovered] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressSvg,setProgressSvg] = useState(0);
  const [counter, setCounter] = useState(Math.floor(duration));
  const [shouldStartNextVideo, setShouldStartNextVideo] = useState(false);

  const intervalProgressRef = useRef<number|null>(null);
  const previousTimeRef = useRef<number|null>();
  const elapsedProgressTimeRef = useRef<number>(0);
  const elapsedCounterTimeRef = useRef<number>(0);

  const animate = (time:number) => {
    if (previousTimeRef.current != null && playbackStatus !== "") {
      const deltaTime = time - previousTimeRef.current;
      elapsedProgressTimeRef.current += deltaTime;
      elapsedCounterTimeRef.current += deltaTime;
      if (elapsedProgressTimeRef.current >= 100) {
        setProgress((prev) => {
          if (prev < 100) {
            if (prev > 80 && progressSvg < 101) {
              const totalTimeNeedToFillSVG =
                (Math.floor(duration) * 1000 * Number(standardWidthSVG)) /
                parseInt(widthMainComponent, 10);
              const percentToAddOnEachInterval = parseFloat(
                (10000 / totalTimeNeedToFillSVG).toFixed(2)
              );
              setProgressSvg((p) => p + percentToAddOnEachInterval);
            }
            return prev + (10 / Math.floor(duration));
          } else {
            cancelAnimationFrame(intervalProgressRef.current || 0);
            // aspetto che il video si stato eseguito del tutto e poi chiamo startNextVideo
            if(playbackStatus === "stop"){
              setShouldStartNextVideo(true);
            }
            return 100;
          }
        });
        elapsedProgressTimeRef.current -= 100;
      }

      if (elapsedCounterTimeRef.current >= 1000 && (playbackStatus === "play" || playbackStatus === "stop")) {
        setProgress((prev) => {
          if (prev >= 0) {
            const newCounterValue = Math.ceil(
              Math.floor(duration) - (prev / 100) * Math.floor(duration)
            );
            setCounter(newCounterValue);
          }
          return prev;
        });
        elapsedCounterTimeRef.current -= 1000;
      }
    }
    previousTimeRef.current = time;
    intervalProgressRef.current = requestAnimationFrame(animate);
  };


  // useEffect per gestire l'avvio del prossimo video fuori dal ciclo di rendering
  useEffect(() => {
    if (shouldStartNextVideo) {
      setShouldStartNextVideo(false);
      startNextVideo();
    }
  }, [shouldStartNextVideo, startNextVideo]);

  useEffect(() => {
    if(playbackStatus === "play"|| playbackStatus === "stop"){
      intervalProgressRef.current = requestAnimationFrame(animate);
    }else{
      cancelAnimationFrame(intervalProgressRef.current||0);
      intervalProgressRef.current = null;
      previousTimeRef.current = null;
      elapsedProgressTimeRef.current = 0;
    }
    return () =>{
      cancelAnimationFrame(intervalProgressRef.current||0);
      intervalProgressRef.current = null;
      previousTimeRef.current = null;
      elapsedProgressTimeRef.current = 0;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playbackStatus]);

  return (
    <ButtonAutoplayBoxStyle
      onMouseEnter={ () => setIsHovered(true) }
      onMouseLeave={ () => setIsHovered(false) }
      onClick={ () =>{
        hideCountDownButton();
        if(onEndAction && playbackStatus === "stop") { onEndAction() }
      }  } >
      { !isMobile &&
        <CounterBoxStyle>
          <Typography sx={ { fontSize:"20px", fontWeight:"800",color:theme.customColors.backgroundPrimaryCta } } variant="button">{ counter }</Typography>
        </CounterBoxStyle>
      }
      <ProgressBoxStyle
        id="progress-box"
        widthMainComponent={ widthMainComponent }
        progress={ progress }
      >
        <div
          style={ {
            display: "block",
            background:theme.customColors.backgroundPrimaryCta,
            WebkitBackgroundClip: "text",
            color: "transparent",
            backgroundImage:`linear-gradient(to right,${theme.customColors.textPrimaryCta} ${progress}%, ${theme.customColors.backgroundPrimaryCta} ${progress}%)`
          } }>
          <div style={ { display:"flex" } }>
            <Box sx={ {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width:"80%"
            } }>
              <Typography
                sx={ {
                  fontSize:"20px",
                  fontWeight:"800"
                } }
              >
                { !isMobile ? isHovered ?  t("close"): t("label_autoplay") : counter }
              </Typography>
            </Box>
            { isHovered ? <IconBoxStyle>
              <Typography sx={ {
                fontSize: "20px",
                fontWeight: "800"
              } } >X</Typography></IconBoxStyle>
              : <IconBoxStyle>
                <IconAutoplay progress={ progressSvg } standardWidth={ standardWidthSVG } icon={ icon }/>
              </IconBoxStyle> }
          </div>
        </div>
      </ProgressBoxStyle>
    </ButtonAutoplayBoxStyle>
  );
}

